'use client';

import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import type { AnimationItem } from 'lottie-web';
import { getAssetUrl, getCompressedAssetUrl } from 'lib/config/assetUrls';
import { useLottieAnimation } from 'hooks/animation/useLottieAnimation';
import { useTranslations } from 'next-intl';
import styles from './LocationAccessPopup.module.css';

const PRECISE_LOCATION_LOTTIE = getAssetUrl(
  '/web_v2/site-static/lottie/precise-location.json',
);
const LOCATION_BG = getCompressedAssetUrl(
  '/web_v2/site-static/location/locationBg.png',
);

export const LocationAccessComponentDiv = ({
  className,
  setFlow,
  setUserLoc,
}: {
  className?: string;
  setFlow?: Dispatch<SetStateAction<string | null>>;
  setUserLoc?: Dispatch<SetStateAction<GeolocationPosition | null>>;
}) => {
  const t = useTranslations('common');
  const animContainerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationItem | null>(null);

  function showPosition(position: GeolocationPosition) {
    if (setUserLoc && setFlow) {
      setUserLoc(position);

      setFlow('LOCATION_PERMISSION_GIVEN');
    }
  }

  useEffect(() => {
    if (animationRef) {
      animationRef.current?.stop();
    }
  }, []);

  useLottieAnimation(animContainerRef, {
    path: PRECISE_LOCATION_LOTTIE,
    renderer: 'svg',
    loop: true,
    autoplay: false,
    onInstance: (animation: AnimationItem) => {
      animationRef.current = animation;
    },
  });

  function getLocation(e: React.MouseEvent<HTMLButtonElement>) {
    console.log(e.target);
    if (e?.target) {
      (e.target as HTMLButtonElement).disabled = true;
      animationRef.current?.play();
    }

    if (window?.navigator) {
      window.navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          showPosition(position);
        },
        (error: GeolocationPositionError) => {
          console.log(error);
          if (setUserLoc && setFlow) {
            setUserLoc(null);
            setFlow('LOCATION_PERMISSION_DENIED');
          }
        },
        {
          timeout: 10000,
        },
      );
    }
  }

  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')}>
      <div
        className={styles.locationImgContainer}
        style={{ backgroundImage: `url(${LOCATION_BG})` }}
      >
        <div className={styles.tracker} ref={animContainerRef} />
      </div>
      <div className={styles.locationContentContainer}>
        <h3>{t('locationAccessPopup.subheading.weNeedAccess')}</h3>
        <p>{t('locationAccessPopup.text.pleaseGrantLocation')}</p>
        <button
          className={`${styles.cta} ${styles.primary}`}
          onClick={getLocation}
        >
          {t('locationAccessPopup.button.gotIt')}
        </button>
      </div>
    </div>
  );
};
